import type { ShipmentEvent } from '@freightapi/core/events/event'
import {
  EFTI_PARTY_ROLES,
  type EftiAuthorisation,
  type EftiPartyRole,
  type EftiPartyType,
  type EftiProcessingEntry,
} from '@freightapi/core/schemas/efti-authorisation'
import type { ProviderTrace } from '@freightapi/core/schemas/provider'
import {
  Button,
  cx,
  DocumentList,
  EmptyState,
  Field,
  formatDate,
  formatDateTime,
  ShipmentView,
  type ShipmentViewSignature,
  Timeline,
} from '@freightapi/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type FormEvent, type ReactNode, useCallback, useState } from 'react'
import { ResourceView } from '../components/ResourceView'
import {
  asApiError,
  type ConsoleDocument,
  type ConsolePod,
  type ConsoleRemark,
  type ConsoleShipment,
  type ConsoleSignature,
  fetchShipment,
  fetchShipmentAuthorisations,
  fetchShipmentDeliveries,
  fetchShipmentDocuments,
  fetchShipmentEvents,
  fetchShipmentIntegrity,
  fetchShipmentPod,
  fetchShipmentProcessingLog,
  fetchShipmentProvider,
  fetchShipmentRemarks,
  fetchShipmentRequestLogs,
  fetchShipmentSignatures,
  grantShipmentAuthorisation,
  type IntegrityReport,
  type Mode,
  type ProviderLink,
  type RequestLogEntry,
  retryProviderSync,
  revokeShipmentAuthorisation,
  type ShipmentEventPage,
  type WebhookDelivery,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useMode } from '../lib/mode'
import { useResource } from '../lib/resource'
import { useCan } from '../lib/session'

/**
 * One shipment, from every angle the console has (M3-04). The tabs are the subjects a support
 * question comes in about: what does the consignment note say, what happened to it, what is stored
 * with it, who signed, what did the receiver confirm, what does the provider know, what did we
 * send to the integration, and what does the raw resource look like.
 *
 * Every tab reads its own endpoint under `/v1/console/shipments/:id/*` when it is opened, so the
 * page costs one request and the expensive reads — the chain, the deliveries, the logs — are paid
 * for by the person who asks for them. The tab lives in the URL, so a link to a broken chain or a
 * failed synchronisation lands on that tab rather than on "overview, now click around".
 *
 * The eFTI tab is deliberately absent; M4-04 adds it. The authorisation tab next to it (M4-16) is
 * about who may touch that data set rather than about what is in it, which is why it is its own
 * subject and not a panel on the eFTI one.
 */

const TABS = [
  'overview',
  'timeline',
  'documents',
  'signatures',
  'pod',
  'provider',
  'authorisations',
  'webhooks',
  'raw',
] as const

type TabId = (typeof TABS)[number]

export const Route = createFileRoute('/_app/shipments/$id')({
  component: ShipmentDetail,
  // Optional, so a link to a shipment does not have to name a tab; an unknown one falls back to
  // the overview instead of turning a shared URL into an error.
  validateSearch: (search: Record<string, unknown>): { tab?: TabId } => {
    const tab = TABS.find((candidate) => candidate === search.tab)
    return tab === undefined ? {} : { tab }
  },
})

function ShipmentDetail() {
  const { id } = Route.useParams()
  const { tab = 'overview' } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { t, locale, sign } = useConsoleI18n()
  const { mode } = useMode()

  const load = useCallback((signal: AbortSignal) => fetchShipment(id, mode, signal), [id, mode])
  const { state } = useResource<ConsoleShipment>(load)

  return (
    <section className="flex flex-col gap-6">
      <div>
        <Link to="/shipments" className="text-body text-brand-700 underline">
          {t('shipments.detail.back')}
        </Link>
      </div>

      <ResourceView
        state={state}
        missing={{
          title: t('shipments.not_found.title'),
          description: t('shipments.not_found.body'),
        }}
      >
        {(shipment) => (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <h1 className="font-semibold text-display text-ink">
                {shipment.reference ?? shipment.id}
              </h1>
              <p className="font-mono text-caption text-muted">{shipment.id}</p>
              <p className="text-caption text-muted">
                {`${sign(`shipment.status.${shipment.status}`)} · ${t('shipments.version', {
                  version: shipment.version,
                })} · ${t('shipments.detail.created', {
                  timestamp: formatDate(shipment.created_at, locale),
                })}`}
              </p>
            </div>

            <div
              role="tablist"
              aria-label={t('shipments.tabs.label')}
              className="flex flex-wrap gap-1 border-line border-b"
            >
              {TABS.map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  role="tab"
                  id={`tab-${candidate}`}
                  aria-selected={tab === candidate}
                  aria-controls={`panel-${candidate}`}
                  onClick={() => void navigate({ search: { tab: candidate }, replace: true })}
                  className={cx(
                    'min-h-touch rounded-t-card px-3 font-medium text-body',
                    tab === candidate
                      ? 'border-brand-600 border-b-2 text-brand-700'
                      : 'text-muted hover:bg-surface',
                  )}
                >
                  {t(`shipments.tab.${candidate}`)}
                </button>
              ))}
            </div>

            <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
              <Panel tab={tab} shipment={shipment} mode={mode} />
            </div>
          </div>
        )}
      </ResourceView>
    </section>
  )
}

function Panel({
  tab,
  shipment,
  mode,
}: {
  readonly tab: TabId
  readonly shipment: ConsoleShipment
  readonly mode: Mode
}) {
  switch (tab) {
    case 'overview':
      return <OverviewTab shipment={shipment} mode={mode} />
    case 'timeline':
      return <TimelineTab id={shipment.id} mode={mode} />
    case 'documents':
      return <DocumentsTab id={shipment.id} mode={mode} />
    case 'signatures':
      return <SignaturesTab id={shipment.id} mode={mode} />
    case 'pod':
      return <PodTab id={shipment.id} mode={mode} />
    case 'provider':
      return <ProviderTab id={shipment.id} mode={mode} />
    case 'authorisations':
      return <AuthorisationsTab id={shipment.id} mode={mode} />
    case 'webhooks':
      return <WebhooksTab id={shipment.id} mode={mode} />
    case 'raw':
      return <RawTab shipment={shipment} />
  }
}

/**
 * The consignment note itself, in the shared component the signer and the inspector see (M2-01).
 * `viewer="console"` is what makes it the version for someone inside the organisation: the same 24
 * boxes, with the contact details a dispatcher needs and an inspector is not shown.
 *
 * Two reads rather than one, because two of the boxes are not part of the shipment resource: the
 * signatures of 22 to 24 and the reservations of 18 are their own resources in the model. They are
 * awaited together so the note appears at once instead of filling in box by box.
 */
function OverviewTab({
  shipment,
  mode,
}: {
  readonly shipment: ConsoleShipment
  readonly mode: Mode
}) {
  const load = useCallback(
    (signal: AbortSignal) => fetchNoteBoxes(shipment.id, mode, signal),
    [shipment.id, mode],
  )
  const { state } = useResource<NoteBoxes>(load)

  return (
    <ResourceView state={state}>
      {({ signatures, remarks }) => (
        <ShipmentView
          shipment={shipment}
          viewer="console"
          status={shipment.status}
          version={shipment.version}
          established_at={shipment.current_version.created_at}
          signatures={signatures.map(toViewSignature)}
          remarks={remarks}
        />
      )}
    </ResourceView>
  )
}

/**
 * The trace of one shipment: its hash chain with the verification of that chain, and under it the
 * API calls that produced it (M3-05). Together they answer the two questions a support case opens
 * with — what happened to this consignment note, and what did the integration do to it.
 */
function TimelineTab({ id, mode }: { readonly id: string; readonly mode: Mode }) {
  const { t, locale, sign } = useConsoleI18n()
  const [trail, setTrail] = useState<readonly ShipmentEvent[]>([])

  const loadIntegrity = useCallback(
    (signal: AbortSignal) => fetchShipmentIntegrity(id, mode, signal),
    [id, mode],
  )
  const integrity = useResource<IntegrityReport>(loadIntegrity)

  // Paging keeps what came before: the chain reads as one list, however many pages it took.
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const loadEvents = useCallback(
    (signal: AbortSignal) => fetchShipmentEvents(id, mode, cursor, signal),
    [id, mode, cursor],
  )
  const events = useResource<ShipmentEventPage>(loadEvents)
  const page = events.state.status === 'ready' ? events.state.value : undefined
  const shown = mergeEvents(trail, page?.data ?? [])

  const loadLogs = useCallback(
    (signal: AbortSignal) => fetchShipmentRequestLogs(id, mode, undefined, signal),
    [id, mode],
  )
  const logs = useResource<{ data: RequestLogEntry[]; next_cursor: string | null }>(loadLogs)

  return (
    <div className="flex flex-col gap-8">
      <ResourceView state={integrity.state}>
        {(report) => (
          <div
            className={cx(
              'flex flex-col gap-1 rounded-card p-4',
              report.ok ? 'bg-ok/15 text-ok' : 'bg-danger/10 text-danger',
            )}
          >
            <p className="font-semibold text-lead">
              {report.ok ? t('shipments.integrity.ok') : t('shipments.integrity.broken')}
            </p>
            <p className="text-body">
              {report.ok
                ? t('shipments.integrity.ok.detail', { events: report.events })
                : t('shipments.integrity.broken.detail', { seq: report.broken_at ?? 0 })}
            </p>
            {report.seq_gaps.length === 0 ? null : (
              <p className="text-body">
                {t('shipments.integrity.gaps', { gaps: report.seq_gaps.join(', ') })}
              </p>
            )}
            <p className="font-mono text-caption">
              {`${t('shipments.integrity.head')}: ${report.head_hash}`}
            </p>
            <p className="text-caption">
              {t('shipments.integrity.verified_at', {
                timestamp: formatDateTime(report.verified_at, locale),
              })}
            </p>
          </div>
        )}
      </ResourceView>

      <ResourceView state={events.state}>
        {() => (
          <div className="flex flex-col gap-6">
            <Timeline entries={shown} />

            <section className="flex flex-col gap-3">
              <h2 className="font-semibold text-lead text-ink">{t('shipments.events.title')}</h2>
              <table className="w-full border-collapse text-left text-body">
                <thead>
                  <tr className="border-line border-b text-caption text-muted">
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.events.column.seq')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.events.column.event')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.events.column.occurred')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.events.column.actor')}
                    </th>
                    <th scope="col" className="py-2 font-medium">
                      {t('shipments.events.column.hash')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((event) => (
                    <tr key={event.id} className="border-line border-b align-top">
                      <td className="py-2 pr-4 font-mono text-caption text-muted">{event.seq}</td>
                      <td className="py-2 pr-4 text-ink">{sign(`event.${event.type}`)}</td>
                      <td className="py-2 pr-4 text-caption text-muted">
                        {formatDateTime(event.occurred_at, locale)}
                      </td>
                      <td className="py-2 pr-4 text-caption text-muted">
                        {event.actor.label ?? sign(`actor.${event.actor.type}`)}
                      </td>
                      <td className="py-2 font-mono text-caption text-muted">
                        <span className="block">{event.hash}</span>
                        <span className="block">
                          {`${t('shipments.events.column.prev_hash')}: ${
                            event.prev_hash === '' ? '—' : event.prev_hash
                          }`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {page?.next_cursor == null ? null : (
                <div>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setTrail(shown)
                      setCursor(page.next_cursor ?? undefined)
                    }}
                  >
                    {t('shipments.more')}
                  </Button>
                </div>
              )}
            </section>
          </div>
        )}
      </ResourceView>

      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-lead text-ink">{t('shipments.trace.title')}</h2>
        <p className="text-body text-muted">{t('shipments.trace.intro')}</p>
        <ResourceView state={logs.state}>
          {(calls) =>
            calls.data.length === 0 ? (
              <p className="text-body text-muted">{t('shipments.trace.empty')}</p>
            ) : (
              <table className="w-full border-collapse text-left text-body">
                <thead>
                  <tr className="border-line border-b text-caption text-muted">
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.trace.column.call')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.trace.column.status')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.trace.column.duration')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.trace.column.request_id')}
                    </th>
                    <th scope="col" className="py-2 font-medium">
                      {t('shipments.trace.column.moment')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {calls.data.map((call) => (
                    <tr key={call.request_id} className="border-line border-b align-top">
                      <td className="py-2 pr-4 font-mono text-caption text-ink">
                        {`${call.method} ${call.path}`}
                      </td>
                      <td
                        className={cx(
                          'py-2 pr-4 font-mono text-caption',
                          call.status >= 400 ? 'text-danger' : 'text-muted',
                        )}
                      >
                        {call.error_code === null
                          ? call.status
                          : `${call.status} ${call.error_code}`}
                      </td>
                      <td className="py-2 pr-4 text-caption text-muted">{`${call.duration_ms} ms`}</td>
                      <td className="py-2 pr-4 font-mono text-caption text-muted">
                        {call.request_id}
                      </td>
                      <td className="py-2 text-caption text-muted">
                        {formatDateTime(call.created_at, locale)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </ResourceView>
      </section>
    </div>
  )
}

/** Everything stored with this shipment, each row a freshly signed five-minute link (M1-06). */
function DocumentsTab({ id, mode }: { readonly id: string; readonly mode: Mode }) {
  const load = useCallback(
    (signal: AbortSignal) => fetchShipmentDocuments(id, mode, signal),
    [id, mode],
  )
  const { state } = useResource<{ data: ConsoleDocument[] }>(load)

  return (
    <ResourceView state={state}>
      {(documents) => (
        <DocumentList
          documents={documents.data}
          hrefFor={(document) =>
            documents.data.find((row) => row.id === document.id)?.download_url ?? '#'
          }
        />
      )}
    </ResourceView>
  )
}

function SignaturesTab({ id, mode }: { readonly id: string; readonly mode: Mode }) {
  const { t, locale, sign } = useConsoleI18n()
  const load = useCallback(
    (signal: AbortSignal) => fetchShipmentSignatureList(id, mode, signal),
    [id, mode],
  )
  const { state } = useResource<readonly ConsoleSignature[]>(load)

  return (
    <ResourceView state={state}>
      {(signatures) =>
        signatures.length === 0 ? (
          <p className="text-body text-muted">{t('shipments.signatures.empty')}</p>
        ) : (
          <table className="w-full border-collapse text-left text-body">
            <thead>
              <tr className="border-line border-b text-caption text-muted">
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.signatures.column.role')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.signatures.column.signer')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.signatures.column.method')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.signatures.column.signed_at')}
                </th>
                <th scope="col" className="py-2 font-medium">
                  {t('shipments.signatures.column.hash')}
                </th>
              </tr>
            </thead>
            <tbody>
              {signatures.map((signature) => (
                <tr key={signature.id} className="border-line border-b align-top">
                  <td className="py-3 pr-4 text-ink">{sign(`sign.role.${signature.role}`)}</td>
                  <td className="py-3 pr-4 text-caption text-muted">
                    <span className="block text-body text-ink">{signature.signer.name}</span>
                    {signature.signer.company === undefined ? null : (
                      <span className="block">{signature.signer.company}</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-caption text-muted">
                    <span className="block">
                      {sign(`shipment.signature.method.${signature.method}`)}
                    </span>
                    <span className="block">
                      {sign(`shipment.signature.trust.${signature.trust_level}`)}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-caption text-muted">
                    {formatDateTime(signature.evidence.signed_at, locale)}
                  </td>
                  <td className="py-3 font-mono text-caption text-muted">
                    <span className="block">{signature.document_hash}</span>
                    <span className={cx('block', signature.verified ? 'text-ok' : 'text-danger')}>
                      {signature.verified
                        ? sign('shipment.signature.verified')
                        : sign('shipment.signature.superseded')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </ResourceView>
  )
}

/** The ePOD of M1-12: what the consignee confirmed, with the reservations and photos at delivery. */
function PodTab({ id, mode }: { readonly id: string; readonly mode: Mode }) {
  const { t, locale } = useConsoleI18n()
  const load = useCallback((signal: AbortSignal) => fetchShipmentPod(id, mode, signal), [id, mode])
  const { state } = useResource<ConsolePod>(load)

  return (
    <ResourceView
      state={state}
      missing={{
        title: t('shipments.pod.empty.title'),
        description: t('shipments.pod.empty.body'),
      }}
    >
      {(pod) => (
        <div className="flex flex-col gap-6">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Entry label={t('shipments.pod.delivered_at')}>
              {formatDateTime(pod.delivered_at, locale)}
            </Entry>
            <Entry label={t('shipments.pod.signed_by')}>
              {`${pod.consignee_signature.signer.name}${
                pod.consignee_signature.signer.company === undefined
                  ? ''
                  : ` · ${pod.consignee_signature.signer.company}`
              }`}
            </Entry>
            <Entry label={t('shipments.pod.colli')}>{pod.colli.expected}</Entry>
            <Entry label={t('shipments.pod.pdf')}>
              {pod.pdf_url === null ? (
                t('shipments.pod.pdf.none')
              ) : (
                <a href={pod.pdf_url} className="text-brand-700 underline">
                  {t('shipments.pod.pdf')}
                </a>
              )}
            </Entry>
          </dl>

          <section className="flex flex-col gap-2">
            <h2 className="font-semibold text-lead text-ink">{t('shipments.pod.remarks')}</h2>
            {pod.remarks.length === 0 ? (
              <p className="text-body text-muted">{t('shipments.pod.remarks.empty')}</p>
            ) : (
              <ul className="flex list-none flex-col gap-2 p-0">
                {pod.remarks.map((remark) => (
                  <li key={remark.id} className="rounded-card border border-line p-3">
                    <p className="text-body text-ink">{remark.text ?? remark.type}</p>
                    <p className="text-caption text-muted">
                      {`${remark.phase} · ${formatDateTime(remark.created_at, locale)}`}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {pod.photos.length === 0 ? null : (
            <section className="flex flex-col gap-2">
              <h2 className="font-semibold text-lead text-ink">{t('shipments.pod.photos')}</h2>
              <DocumentList
                documents={pod.photos}
                hrefFor={(document) =>
                  pod.photos.find((photo) => photo.id === document.id)?.download_url ?? '#'
                }
              />
            </section>
          )}
        </div>
      )}
    </ResourceView>
  )
}

/**
 * The provider side (M2-15): the link, where the last synchronisation stands, every step it
 * recorded, and the button that runs it again. The steps come out of the chain — a trace is a
 * `shipment.updated` on `provider_link` — so this tab and the timeline never disagree.
 */
function ProviderTab({ id, mode }: { readonly id: string; readonly mode: Mode }) {
  const { t, locale } = useConsoleI18n()
  const can = useCan()
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const loadLink = useCallback(
    (signal: AbortSignal) => fetchShipmentProvider(id, mode, signal),
    [id, mode],
  )
  const link = useResource<ProviderLink>(loadLink)

  const loadEvents = useCallback(
    (signal: AbortSignal) => fetchShipmentEvents(id, mode, undefined, signal),
    [id, mode],
  )
  const events = useResource<ShipmentEventPage>(loadEvents)
  const traces = events.state.status === 'ready' ? tracesOf(events.state.value.data) : []

  async function onRetry() {
    setNotice(undefined)
    setFailure(undefined)
    setBusy(true)
    try {
      await retryProviderSync(id, mode)
      setNotice(t('shipments.provider.retry.started'))
      link.reload()
      events.reload()
    } catch (cause) {
      const error = asApiError(cause)
      setFailure(error.problem?.detail ?? t(error.messageKey))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ResourceView
      state={link.state}
      missing={{
        title: t('shipments.provider.empty.title'),
        description: t('shipments.provider.empty.body'),
      }}
    >
      {(current) => (
        <div className="flex flex-col gap-6">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Entry label={t('shipments.provider.provider')}>{current.provider}</Entry>
            <Entry label={t('shipments.provider.status')}>
              <span
                className={cx(
                  'rounded-card px-2 py-0.5',
                  current.sync_status === 'failed' ? 'bg-danger/10 text-danger' : 'bg-canvas',
                )}
              >
                {t(`shipments.provider.status.${current.sync_status}`)}
              </span>
            </Entry>
            <Entry label={t('shipments.provider.external_id')}>{current.external_id ?? '—'}</Entry>
            <Entry label={t('shipments.provider.last_operation')}>
              {current.last_operation ?? '—'}
            </Entry>
            <Entry label={t('shipments.provider.last_synced')}>
              {current.last_synced_at === null
                ? '—'
                : formatDateTime(current.last_synced_at, locale)}
            </Entry>
            <Entry label={t('shipments.provider.error')}>
              {current.last_error === null
                ? '—'
                : `${current.last_error.code}: ${current.last_error.message}`}
            </Entry>
          </dl>

          {notice === undefined ? null : (
            <p role="status" className="text-body text-muted">
              {notice}
            </p>
          )}
          {failure === undefined ? null : (
            <p role="alert" className="text-body text-danger">
              {failure}
            </p>
          )}

          {can('shipments:write') ? (
            <div>
              <Button busy={busy} busyLabel={t('loading')} onClick={() => void onRetry()}>
                {t('shipments.provider.retry')}
              </Button>
            </div>
          ) : null}

          <section className="flex flex-col gap-3">
            <h2 className="font-semibold text-lead text-ink">
              {t('shipments.provider.trace.title')}
            </h2>
            {traces.length === 0 ? (
              <p className="text-body text-muted">{t('shipments.provider.trace.empty')}</p>
            ) : (
              <table className="w-full border-collapse text-left text-body">
                <thead>
                  <tr className="border-line border-b text-caption text-muted">
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.provider.trace.column.step')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.provider.trace.column.outcome')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.provider.trace.column.detail')}
                    </th>
                    <th scope="col" className="py-2 font-medium">
                      {t('shipments.provider.trace.column.moment')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {traces.map((entry) => (
                    <tr key={entry.event_id} className="border-line border-b align-top">
                      <td className="py-2 pr-4 text-ink">
                        {t(`shipments.provider.step.${entry.trace.step}`)}
                      </td>
                      <td
                        className={cx(
                          'py-2 pr-4 text-caption',
                          entry.trace.outcome === 'failed' ? 'text-danger' : 'text-muted',
                        )}
                      >
                        {t(`shipments.provider.outcome.${entry.trace.outcome}`)}
                      </td>
                      <td className="py-2 pr-4 text-caption text-muted">
                        {entry.trace.error === undefined
                          ? (entry.trace.detail ?? '—')
                          : `${entry.trace.error.code}: ${entry.trace.error.message}`}
                      </td>
                      <td className="py-2 text-caption text-muted">
                        {formatDateTime(entry.occurred_at, locale)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      )}
    </ResourceView>
  )
}

/** What Cargofollow sent to the integration about this shipment, out of the M1-14 delivery log. */
function WebhooksTab({ id, mode }: { readonly id: string; readonly mode: Mode }) {
  const { t, locale } = useConsoleI18n()
  const load = useCallback(
    (signal: AbortSignal) => fetchShipmentDeliveries(id, mode, undefined, signal),
    [id, mode],
  )
  const { state } = useResource<{ data: WebhookDelivery[]; next_cursor: string | null }>(load)

  return (
    <ResourceView state={state}>
      {(page) =>
        page.data.length === 0 ? (
          <EmptyState title={t('shipments.webhooks.empty')} />
        ) : (
          <table className="w-full border-collapse text-left text-body">
            <thead>
              <tr className="border-line border-b text-caption text-muted">
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.webhooks.column.event')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.webhooks.column.status')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.webhooks.column.attempt')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.webhooks.column.response')}
                </th>
                <th scope="col" className="py-2 font-medium">
                  {t('shipments.webhooks.column.moment')}
                </th>
              </tr>
            </thead>
            <tbody>
              {page.data.map((delivery) => (
                <tr key={delivery.id} className="border-line border-b align-top">
                  <td className="py-2 pr-4 font-mono text-caption text-ink">
                    {delivery.event_type}
                  </td>
                  <td
                    className={cx(
                      'py-2 pr-4 text-caption',
                      delivery.status === 'succeeded' ? 'text-muted' : 'text-danger',
                    )}
                  >
                    {t(`shipments.webhooks.status.${delivery.status}`)}
                  </td>
                  <td className="py-2 pr-4 text-caption text-muted">
                    {`${delivery.attempt}/${delivery.max_attempts}`}
                  </td>
                  <td className="py-2 pr-4 text-caption text-muted">
                    {delivery.error ??
                      (delivery.response_status === null
                        ? '—'
                        : `HTTP ${delivery.response_status}${
                            delivery.duration_ms === null ? '' : ` · ${delivery.duration_ms} ms`
                          }`)}
                  </td>
                  <td className="py-2 text-caption text-muted">
                    {formatDateTime(delivery.delivered_at ?? delivery.created_at, locale)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </ResourceView>
  )
}

/** The resource as the API returns it, for the developer who is comparing it with their own code. */
function RawTab({ shipment }: { readonly shipment: ConsoleShipment }) {
  const { t } = useConsoleI18n()
  const [copied, setCopied] = useState(false)
  const json = JSON.stringify(shipment, null, 2)

  return (
    <div className="flex flex-col gap-3">
      <p className="text-body text-muted">{t('shipments.raw.intro')}</p>
      <div>
        <Button
          variant="ghost"
          onClick={() => {
            void navigator.clipboard.writeText(json).then(() => setCopied(true))
          }}
        >
          {copied ? t('shipments.raw.copied') : t('shipments.raw.copy')}
        </Button>
      </div>
      <pre className="overflow-x-auto rounded-card border border-line bg-surface p-4 font-mono text-caption text-ink">
        {json}
      </pre>
    </div>
  )
}

function Entry({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-caption text-muted">{label}</dt>
      <dd className="text-body text-ink">{children}</dd>
    </div>
  )
}

/** The boxes of the consignment note that are resources of their own: 18, and 22 to 24. */
interface NoteBoxes {
  readonly signatures: readonly ConsoleSignature[]
  readonly remarks: readonly ConsoleRemark[]
}

function fetchNoteBoxes(id: string, mode: Mode, signal: AbortSignal): Promise<NoteBoxes> {
  return Promise.all([
    fetchShipmentSignatureList(id, mode, signal),
    fetchShipmentRemarks(id, mode, signal).then((page) => page.data),
  ]).then(([signatures, remarks]) => ({ signatures, remarks }))
}

/** Two tabs want the signatures; both want them as a plain list rather than as `{ data }`. */
function fetchShipmentSignatureList(
  id: string,
  mode: Mode,
  signal: AbortSignal,
): Promise<readonly ConsoleSignature[]> {
  return fetchShipmentSignatures(id, mode, signal).then((page) => page.data)
}

function toViewSignature(signature: ConsoleSignature): ShipmentViewSignature {
  return {
    role: signature.role,
    method: signature.method,
    trust_level: signature.trust_level,
    signer: signature.signer,
    signed_at: signature.evidence.signed_at,
    document_hash: signature.document_hash,
    verified: signature.verified,
  }
}

/** Appends a page to what is already on screen, without letting a re-read double a row. */
function mergeEvents(
  earlier: readonly ShipmentEvent[],
  page: readonly ShipmentEvent[],
): readonly ShipmentEvent[] {
  const seen = new Set(earlier.map((event) => event.id))
  return [...earlier, ...page.filter((event) => !seen.has(event.id))]
}

interface TraceEntry {
  readonly event_id: string
  readonly occurred_at: string
  readonly trace: ProviderTrace
}

function tracesOf(events: readonly ShipmentEvent[]): readonly TraceEntry[] {
  const entries: TraceEntry[] = []
  for (const event of events) {
    const trace = event.payload.trace
    if (isTrace(trace)) {
      entries.push({ event_id: event.id, occurred_at: event.occurred_at, trace })
    }
  }
  return entries
}

function isTrace(value: unknown): value is ProviderTrace {
  return typeof value === 'object' && value !== null && 'step' in value && 'outcome' in value
}

/**
 * The authorisation register of this data set (M4-16, IR (EU) 2025/2243 Art. 5).
 *
 * Two tables, because the regulation asks for two different things. The register says who *may* do
 * what — including the entries that were withdrawn, which Art. 5(6) keeps readable for as long as
 * the data set lives. The processing log underneath says what was actually done, refusals
 * included, which is Art. 9(5) and the only way a withdrawal can be shown to have had an effect.
 *
 * The data holder's own entry is drawn without a withdraw button: it is what keeps this
 * organisation able to read its own consignment note, and the API refuses to take it away.
 */
function AuthorisationsTab({ id, mode }: { readonly id: string; readonly mode: Mode }) {
  const { t, locale } = useConsoleI18n()
  const can = useCan()
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const [notice, setNotice] = useState<string | undefined>(undefined)

  const loadRegister = useCallback(
    (signal: AbortSignal) => fetchShipmentAuthorisations(id, mode, signal),
    [id, mode],
  )
  const register = useResource<{ data: EftiAuthorisation[]; dataset_uuid: string | null }>(
    loadRegister,
  )

  const loadLog = useCallback(
    (signal: AbortSignal) => fetchShipmentProcessingLog(id, mode, signal),
    [id, mode],
  )
  const log = useResource<{ data: EftiProcessingEntry[] }>(loadLog)

  const run = useCallback(
    async (action: () => Promise<unknown>, done: string) => {
      setFailure(undefined)
      setNotice(undefined)
      try {
        await action()
        setNotice(done)
        register.reload()
        log.reload()
        return true
      } catch (cause) {
        const error = asApiError(cause)
        setFailure(error.problem?.detail ?? t(error.messageKey))
        return false
      }
    },
    [log, register, t],
  )

  const write = can('shipments:write')

  return (
    <ResourceView
      state={register.state}
      missing={{
        title: t('shipments.authorisations.empty.title'),
        description: t('shipments.authorisations.empty.body'),
      }}
    >
      {(current) => (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <h2 className="font-semibold text-lead text-ink">
              {t('shipments.authorisations.title')}
            </h2>
            <p className="text-body text-muted">{t('shipments.authorisations.intro')}</p>
            <p className="break-all font-mono text-caption text-muted">
              {current.dataset_uuid === null
                ? t('shipments.authorisations.dataset.none')
                : t('shipments.authorisations.dataset', { uuid: current.dataset_uuid })}
            </p>
          </div>

          {failure === undefined ? null : (
            <p role="alert" className="text-body text-danger">
              {failure}
            </p>
          )}
          {notice === undefined ? null : (
            <p role="status" className="text-body text-muted">
              {notice}
            </p>
          )}

          <table className="w-full border-collapse text-left text-body">
            <thead>
              <tr className="border-line border-b text-caption text-muted">
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.authorisations.column.party')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.authorisations.column.role')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.authorisations.column.operations')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.authorisations.column.window')}
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  {t('shipments.authorisations.column.status')}
                </th>
                <th scope="col" className="py-2 font-medium">
                  {t('shipments.authorisations.column.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {current.data.map((entry) => (
                <tr key={entry.id} className="border-line border-b align-top">
                  <td className="py-2 pr-4">
                    <span className="block break-all text-ink">
                      {entry.party.label ?? entry.party.contact ?? entry.party.ref}
                    </span>
                    <span className="block text-caption text-muted">
                      {`${t(`shipments.authorisations.party_type.${entry.party.type}`)} · ${t(
                        `shipments.authorisations.source.${entry.source}`,
                      )}`}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-ink">
                    {t(`shipments.authorisations.role.${entry.role}`)}
                  </td>
                  <td className="py-2 pr-4 text-caption text-muted">
                    <span className="block">
                      {entry.operations
                        .map((operation) => t(`shipments.processing.operation.${operation}`))
                        .join(', ')}
                    </span>
                    <span className="block">
                      {entry.data_scope.includes('*')
                        ? t('shipments.authorisations.scope.all')
                        : entry.data_scope.join(', ')}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-caption text-muted">
                    {`${formatDate(entry.valid_from, locale)} — ${
                      entry.valid_until === null
                        ? t('shipments.authorisations.until.none')
                        : formatDate(entry.valid_until, locale)
                    }`}
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={cx(
                        'rounded-card px-2 py-0.5 text-caption',
                        entry.status === 'active'
                          ? 'bg-canvas text-ink'
                          : 'bg-danger/10 text-danger',
                      )}
                    >
                      {t(`shipments.authorisations.status.${entry.status}`)}
                    </span>
                  </td>
                  <td className="py-2">
                    {write && entry.source !== 'baseline' && entry.status !== 'revoked' ? (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          if (!window.confirm(t('shipments.authorisations.revoke.confirm'))) return
                          void run(
                            () => revokeShipmentAuthorisation(id, entry.id, mode),
                            t('shipments.authorisations.revoke.done'),
                          )
                        }}
                      >
                        {t('shipments.authorisations.revoke')}
                      </Button>
                    ) : (
                      <span className="text-caption text-muted">
                        {entry.source === 'baseline' ? t('shipments.authorisations.baseline') : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {write ? (
            <GrantForm
              onGrant={(body) =>
                run(
                  () => grantShipmentAuthorisation(id, mode, body),
                  t('shipments.authorisations.grant.done'),
                )
              }
            />
          ) : null}

          <section className="flex flex-col gap-3">
            <h2 className="font-semibold text-lead text-ink">{t('shipments.processing.title')}</h2>
            <p className="text-body text-muted">{t('shipments.processing.intro')}</p>
            {log.state.status !== 'ready' || log.state.value.data.length === 0 ? (
              <p className="text-body text-muted">{t('shipments.processing.empty')}</p>
            ) : (
              <table className="w-full border-collapse text-left text-body">
                <thead>
                  <tr className="border-line border-b text-caption text-muted">
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.processing.column.moment')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.processing.column.operation')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.processing.column.party')}
                    </th>
                    <th scope="col" className="py-2 font-medium">
                      {t('shipments.processing.column.outcome')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {log.state.value.data.map((entry) => (
                    <tr key={entry.id} className="border-line border-b align-top">
                      <td className="py-2 pr-4 text-caption text-muted">
                        {formatDateTime(entry.created_at, locale)}
                      </td>
                      <td className="py-2 pr-4 text-ink">
                        {t(`shipments.processing.operation.${entry.operation}`)}
                      </td>
                      <td className="py-2 pr-4 text-caption text-muted">
                        <span className="block break-all">
                          {entry.party.label ?? entry.party.contact ?? entry.party.ref}
                        </span>
                        <span className="block">
                          {t(`shipments.authorisations.party_type.${entry.party.type}`)}
                        </span>
                      </td>
                      <td
                        className={cx(
                          'py-2 text-caption',
                          entry.outcome === 'refused' ? 'text-danger' : 'text-muted',
                        )}
                      >
                        {t(`shipments.processing.outcome.${entry.outcome}`)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      )}
    </ResourceView>
  )
}

/** The party types a human may grant to; `authority` is recorded by the gate, never handed out. */
const GRANTABLE_PARTY_TYPES = ['contact', 'organization', 'member', 'api_key'] as const

type GrantablePartyType = (typeof GRANTABLE_PARTY_TYPES)[number]

interface GrantBody {
  readonly party_type: EftiPartyType
  readonly party_ref?: string
  readonly email?: string
  readonly role: EftiPartyRole
  readonly valid_until?: string
}

/**
 * The grant form. Two fields decide the shape of the rest: a non-onboarded party is named by an
 * e-mail address and must be given an end date (Art. 5(5)(a)(iv)), an onboarded one is named by
 * its Cargofollow id and may be open-ended. Operations are deliberately not on this form — the API
 * fills in what the role needs, which is the answer in almost every case and the one a dispatcher
 * cannot get subtly wrong.
 */
function GrantForm({ onGrant }: { readonly onGrant: (body: GrantBody) => Promise<boolean> }) {
  const { t } = useConsoleI18n()
  const [partyType, setPartyType] = useState<GrantablePartyType>('contact')
  const [reference, setReference] = useState('')
  const [role, setRole] = useState<EftiPartyRole>('carrier')
  const [until, setUntil] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    const trimmed = reference.trim()
    const granted = await onGrant({
      party_type: partyType,
      ...(partyType === 'contact' ? { email: trimmed } : { party_ref: trimmed }),
      role,
      // The end of a day rather than its start: a right that runs "until the 20th" and stops
      // working on the morning of the 20th is a support ticket, not a policy.
      ...(until === '' ? {} : { valid_until: new Date(`${until}T23:59:59.000Z`).toISOString() }),
    })
    setBusy(false)
    if (granted) {
      setReference('')
      setUntil('')
    }
  }

  return (
    <form
      className="flex flex-col gap-4 rounded-card border border-line p-4"
      onSubmit={(event) => void onSubmit(event)}
    >
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-lead text-ink">
          {t('shipments.authorisations.grant.title')}
        </h2>
        <p className="text-body text-muted">{t('shipments.authorisations.grant.intro')}</p>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:items-end">
        <Field label={t('shipments.authorisations.grant.party_type')}>
          {({ id, describedBy }) => (
            <select
              id={id}
              aria-describedby={describedBy}
              value={partyType}
              onChange={(event) => setPartyType(event.target.value as GrantablePartyType)}
              className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink"
            >
              {GRANTABLE_PARTY_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`shipments.authorisations.party_type.${value}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field
          label={
            partyType === 'contact'
              ? t('shipments.authorisations.grant.email')
              : t('shipments.authorisations.grant.party_ref')
          }
          required
          className="grow"
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              type={partyType === 'contact' ? 'email' : 'text'}
              required
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              className="min-h-touch w-full rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <Field label={t('shipments.authorisations.grant.role')}>
          {({ id, describedBy }) => (
            <select
              id={id}
              aria-describedby={describedBy}
              value={role}
              onChange={(event) => setRole(event.target.value as EftiPartyRole)}
              className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink"
            >
              {EFTI_PARTY_ROLES.filter((value) => value !== 'data_holder').map((value) => (
                <option key={value} value={value}>
                  {t(`shipments.authorisations.role.${value}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field
          label={t('shipments.authorisations.grant.valid_until')}
          {...(partyType === 'contact'
            ? { hint: t('shipments.authorisations.grant.valid_until.hint') }
            : {})}
        >
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              type="date"
              required={partyType === 'contact'}
              value={until}
              onChange={(event) => setUntil(event.target.value)}
              className="min-h-touch rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <Button type="submit" busy={busy} busyLabel={t('loading')}>
          {t('shipments.authorisations.grant.submit')}
        </Button>
      </div>
    </form>
  )
}
